<template>
  <div class="custom-form custom-form--edit-password">
    <div class="custom-form__header">
      <slot name="content-introduction"/>
    </div>
    <form
      @submit.prevent="submit"
      novalidate autocomplete="off">
      <div class="fieldset">
        <field-input
          v-model="form.passwordCurrent"
          :v="$v.form.passwordCurrent"
          :field="fieldPasswordCurrent"/>
      </div>
      <div class="fieldset">
        <field-input
          :live-validation="true"
          v-model="form.passwordNew"
          :v="$v.form.passwordNew"
          :field="fieldPasswordNew"/>
      </div>
      <div class="fieldset">
        <field-input
          v-model="form.passwordNewConfirm"
          :v="$v.form.passwordNewConfirm"
          :field="fieldPasswordNewConfirm"/>
      </div>
      <div class="custom-form__actions">
        <captcha
          ref="captcha"
          v-if="fieldCaptcha.isVisible"
          :captcha-id="fieldCaptcha.id"
          :captcha-sitekey="fieldCaptcha.siteKey"
          :explicitly-render="true"
          @captchaError="(value) => this.editpassgetCaptchaError(value)"
        >
          <p
            class="field-help"
            v-if="hasErrorCapcha"
            :id="fieldCaptcha.idError">
            {{ fieldCaptcha.error }}
          </p>
        </captcha>

        <div :class="ctaClass" v-if="!ctaLink">
          <a
            :href="template.ctaCancelUrl"
            title="Link"
            class="btn__wrapper btn__wrapper--cross">
            {{ template.ctaCancel }}
          </a>
        </div>
        <div>
          <template v-if="!serverResponse && serverError === null">
            <button
              type="submit"
              :class="[ctaClass, { 'btn--default-w': ctaLink }, { 'disabled': $v.form.$invalid }]"
              :disabled="$v.form.$invalid"
              v-tag:useractionevent="{
                label: 'save',
                category: 'my account',
                action: 'edit my password',
                event_name: 'edit_password'
              }">
              <span class="btn__wrapper">{{ template.ctaSubmit }}</span>
            </button>
          </template>

          <template v-else>
            <a :href="ctaLink" :class="ctaClass">
              <span class="btn__wrapper">{{ template.ctaSuccess }}</span>
            </a>
          </template>
        </div>

        <div role="alert">
          <p v-if="serverResponse !== null"
             class="custom-form__response"
             :class="{ 'error': !serverResponse }">{{ serverResponse ? template.succeed : template.failure.default }}</p>
          <p v-if="serverError !== null"
             class="custom-form__response error">{{ template.error }}</p>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
  import { helpers, minLength, required, sameAs, not } from 'vuelidate/lib/validators';
  import { tag } from '@Foundation/analyticsHandler';
  import { validationMixin } from 'vuelidate';
  import { postData } from '@Foundation/services';
  import FieldInput from '@Feature/CustomForms/fields/FieldInput/code/Scripts/components/field-input.vue';
  import Captcha from '@Feature/Captcha/code/Scripts/components/captcha.vue';

  /**
   * Custom validators vor Vuelidate
   */
  const atLeastOneNumber = helpers.regex('atLeastOneNumber', /(?=.*[0-9])/);
  const atLeastOneSymbol = helpers.regex('atLeastOneSymbol', /(?=.*\W)/); // at least one non-word character exists
  const atLeastOneUppercaseCharacter = helpers.regex('atLeastOneUppercaseCharacter', /(?=.*[A-Z-А-Я])/);

  export default {
    name: 'custom-form-edit-password',

    components: { FieldInput, Captcha },

    directives: { tag },

    mixins: [validationMixin],

    props: {
      fieldPasswordCurrent: { type: Object, required: true, default: undefined },
      fieldPasswordNew: { type: Object, required: true, default: undefined },
      fieldPasswordNewConfirm: { type: Object, required: true, default: undefined },
      fieldCaptcha: { type: Object, required: false, default: undefined },
      template: { type: Object, required: true, default: undefined },
      apiUrl: { type: String, required: true, default: undefined },
      ctaClass: { type: String, default: 'btn btn--black' },
    },

    data() {
      return {
        form: {
          passwordCurrent: '',
          passwordNew: '',
          passwordNewConfirm: ''
        },
        ctaLink: null,
        serverResponse: null,
        serverError: null,
        hasErrorCapcha: false
      };
    },

    /**
     * Vuelidate config
     * @see {@link https://github.com/monterail/vuelidate|GitHub}
     */
    validations() {
      return {
        form: {
          passwordCurrent: {
            required
          },
          passwordNew: {
            required,
            notSameAsPassword: not(sameAs('passwordCurrent')),
            minLength: minLength(8),
            atLeastOneUppercaseCharacter, // Custom
            atLeastOneNumber, // Custom
            atLeastOneSymbol, // Custom
          },
          passwordNewConfirm: {
            sameAsPassword: sameAs('passwordNew'),
          },
        }
      };
    },

    methods: {
      editpassgetCaptchaError(val) {
        this.hasErrorCapcha = val;
      },

      /** check if captcha required for validation before sending a request
       * @param {function} callback - function to call after validation
       */
      editpassvalidateCapcha(callback) {
        if (this.fieldCaptcha.isVisible) {
          this.$refs.captcha.checkCaptcha();

          this.$nextTick(() => {
            /* istanbul ignore else */
            if (!this.hasErrorCapcha) {
              callback();
            }
          });

        } else {
          callback();
        }
      },

      editpasssendRequest() {
        postData(`${window.location.origin}/${this.apiUrl}`, {
          'passwordCurrent': this.form.passwordCurrent,
          'passwordNew': this.form.passwordNew
        })
          .then((response) => {
            this.serverResponse = response.status;
            this.ctaLink = response.url;
          })
          .catch(() => {
            this.serverError = true;
          });
      },

      /**
       * @returns {undefined}
       */
      submit() {
        this.$v.form.$touch();
        if (this.$v.form.$pending || this.$v.form.$error) {
          return;
        } else {
          this.editpassvalidateCapcha(this.editpasssendRequest);
        }
      },
    }
  };
</script>

<style lang='scss' src="./custom-form-edit-password.scss"></style>
